'use client';

import React, { useEffect, useState } from 'react';
import { Globe, X } from 'lucide-react';
import { useLanguage, LOCALE_NATIVE_NAMES, detectBrowserLocale, type UILocale } from '@/i18n/LanguageContext';

const DISMISSED_KEY = 'ertzyx_lang_prompt_dismissed';
const STORAGE_KEY = 'ertzyx_ui_locale';

function detectBrowserSuggestion(): UILocale | null {
  const detected = detectBrowserLocale();
  // Never suggest switching away from English — the banner only exists to offer a
  // non-English alternative when one is clearly available.
  return detected === 'en' ? null : detected;
}

export default function LanguageSuggestionBanner() {
  const { locale, setLocale } = useLanguage();
  const [suggestion, setSuggestion] = useState<UILocale | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(DISMISSED_KEY);
      if (dismissed) return;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return;
    } catch {
      return;
    }

    const suggested = detectBrowserSuggestion();
    if (suggested && suggested !== locale) {
      setSuggestion(suggested);
      setVisible(true);
    }
  }, [locale]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  const accept = () => {
    if (suggestion) {
      setLocale(suggestion);
    }
    dismiss();
  };

  const keepEnglish = () => {
    setLocale('en');
    dismiss();
  };

  if (!visible || !suggestion) return null;

  return (
    <div
      role="dialog"
      aria-label="Language suggestion"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        maxWidth: 420,
        width: 'calc(100vw - 32px)',
        background: '#0d1117',
        border: '1px solid rgba(0,180,160,0.25)',
        borderRadius: 14,
        boxShadow: '0 8px 40px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: '#e8edf3',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 10,
          background: 'rgba(0,180,160,0.12)',
          border: '1px solid rgba(0,180,160,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Globe size={17} color="#00b4a0" />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#e8edf3', margin: 0, lineHeight: 1.35 }}>
          Use {LOCALE_NATIVE_NAMES[suggestion]}?
        </p>
        <p style={{ fontSize: 11, color: '#4a5d72', margin: '2px 0 0', lineHeight: 1.3 }}>
          We detected your browser language.
        </p>
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={accept}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '5px 12px',
              borderRadius: 8,
              background: '#00b4a0',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Use {LOCALE_NATIVE_NAMES[suggestion]}
          </button>
          <button
            type="button"
            onClick={keepEnglish}
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '5px 12px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.07)',
              color: '#8ba3bc',
              border: '1px solid rgba(255,255,255,0.10)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Keep English
          </button>
        </div>
      </div>

      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#4a5d72',
          padding: 4,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <X size={15} />
      </button>
    </div>
  );
}
